import type { ColumnDef } from '@tanstack/react-table';
import { Eye, MoreHorizontal, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { HighlightText } from '@/components/common/HighlightText';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDisplayDate } from '@/utils/date';
import { formatCount } from '@/utils/number';
import type { PcmpProjectListItem } from '../types/pcmpProject.types';
import { PcmpProjectStatusBadge } from './PcmpProjectStatusBadge';
import { PcmpProjectVisibility } from './PcmpProjectVisibility';

export interface PcmpProjectColumnActions {
  onView: (project: PcmpProjectListItem) => void;
  onEdit: (project: PcmpProjectListItem) => void;
  onToggleActive: (project: PcmpProjectListItem) => void;
  onDelete: (project: PcmpProjectListItem) => void;
}

const emptyCell = <span className="text-muted-foreground">—</span>;

export function getPcmpProjectColumns(
  searchText: string | undefined,
  actions: PcmpProjectColumnActions,
): ColumnDef<PcmpProjectListItem>[] {
  return [
    {
      accessorKey: 'displayName',
      header: 'Project',
      cell: ({ row }) => (
        <div className="max-w-80 truncate" title={row.original.displayName}>
          <span className="font-medium">
            <HighlightText text={row.original.displayName} highlightedText={searchText} />
          </span>
          {row.original.code ? (
            <div className="truncate font-mono text-xs text-muted-foreground">
              <HighlightText text={row.original.code} highlightedText={searchText} />
            </div>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'companyName',
      header: 'Company',
      cell: ({ row }) => row.original.companyName ?? emptyCell,
    },
    {
      accessorKey: 'bookEditionName',
      header: 'Edition',
      cell: ({ row }) => row.original.bookEditionName ?? emptyCell,
    },
    {
      id: 'activeWindow',
      header: 'Active Window',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm">
          {formatDisplayDate(row.original.activeFrom)} – {formatDisplayDate(row.original.activeTo)}
        </span>
      ),
    },
    {
      id: 'visibility',
      header: 'Visible On',
      cell: ({ row }) => (
        <PcmpProjectVisibility
          isWebVisible={row.original.isWebVisible}
          isMobileVisible={row.original.isMobileVisible}
        />
      ),
    },
    {
      accessorKey: 'priceCount',
      header: 'Price Grid',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
          {formatCount(row.original.categoryCount, 'category')} ·{' '}
          {formatCount(row.original.priceCount, 'price')}
        </span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <PcmpProjectStatusBadge isActive={row.original.isActive} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div onClick={(event) => event.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={`Actions for ${row.original.displayName}`}
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              }
            />
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={() => actions.onView(row.original)}>
                <Eye className="size-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => actions.onEdit(row.original)}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => actions.onToggleActive(row.original)}>
                {row.original.isActive ? (
                  <PowerOff className="size-4" />
                ) : (
                  <Power className="size-4" />
                )}
                {row.original.isActive ? 'Deactivate' : 'Activate'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => actions.onDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];
}
